// skipcq: JS-W1028, JS-0128
import React, { useId, useMemo } from 'react';
import {
  CardContent,
  CardActions,
  Typography,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  Customized,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useSplatsTotal from '../../hooks/useSplatsTotal';
import { CHART_SEGMENT_COLORS } from '../../utils/chartPalette';
import ChartSegmentPatternDefs, {
  chartPatternFragmentId,
} from '../widgets/ChartSegmentPatternDefs';
import DashboardSurfaceCard from './DashboardSurfaceCard';

function SplatBarPatternDefs({ uniqueId, segments = [] }) {
  return <ChartSegmentPatternDefs uniqueId={uniqueId} segments={segments} />;
}

const ROW_HEIGHT_PX = 32;
const CHART_VERTICAL_MARGIN = 48;
const CHART_MIN_HEIGHT = 120;
const CHART_SCROLL_MAX = 280;

function formatSplatAwardedTooltipValue(value) {
  return [Number(value).toLocaleString(), 'Awarded'];
}

function SplatAwardedBarWithCells({ data, chartPatternUid }) {
  return (
    <Bar dataKey="awarded" radius={[0, 4, 4, 0]} maxBarSize={28}>
      {data.map((row) => (
        <Cell
          key={row.type}
          fill={`url(#${chartPatternFragmentId(chartPatternUid, row.type)})`}
        />
      ))}
    </Bar>
  );
}

function SplatTypeBreakdownRecharts({ data, chartPatternUid, barPatternSegments, tooltipContentStyle }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
      >
        <Customized
          component={SplatBarPatternDefs}
          uniqueId={chartPatternUid}
          segments={barPatternSegments}
        />
        <XAxis type="number" tick={{ fontSize: 12 }} />
        <YAxis
          type="category"
          dataKey="type"
          width={110}
          tick={{ fontSize: 12 }}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          formatter={formatSplatAwardedTooltipValue}
        />
        <SplatAwardedBarWithCells data={data} chartPatternUid={chartPatternUid} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function SplatTypeBreakdownBarChart({ data, chartHeight, tooltipContentStyle }) {
  const chartPatternUid = useId().replace(/:/g, '');

  const barPatternSegments = useMemo(
    () =>
      data.map((row, index) => ({
        segmentKey: row.type,
        fill: CHART_SEGMENT_COLORS[index % CHART_SEGMENT_COLORS.length],
      })),
    [data],
  );

  return (
    <Box
      data-testid="splat-type-breakdown-chart"
      sx={{
        width: '100%',
        maxHeight: CHART_SCROLL_MAX,
        overflowY: 'auto',
        mt: 2,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ width: '100%', height: chartHeight, minHeight: CHART_MIN_HEIGHT }}>
        <SplatTypeBreakdownRecharts
          data={data}
          chartPatternUid={chartPatternUid}
          barPatternSegments={barPatternSegments}
          tooltipContentStyle={tooltipContentStyle}
        />
      </Box>
    </Box>
  );
}

/**
 * Total Splats awarded and breakdown by `type` (splatDefinitions).
 */
function SplatManagementCard() {
  const theme = useTheme();
  const { totalSplatsAwarded, awardedByType, loading, error } = useSplatsTotal();
  const barTooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  const chartHeight = useMemo(() => {
    if (!awardedByType.length) return CHART_MIN_HEIGHT;
    return Math.max(
      CHART_MIN_HEIGHT,
      awardedByType.length * ROW_HEIGHT_PX + CHART_VERTICAL_MARGIN,
    );
  }, [awardedByType.length]);

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Splat Management
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Total Splats awarded across all types
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
              <Typography
                variant="h4"
                component="div"
                color="primary"
                sx={{ mr: 2 }}
              >
                {totalSplatsAwarded.toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Splats awarded
            </Typography>

            {awardedByType.length > 0 ? (
              <SplatTypeBreakdownBarChart
                data={awardedByType}
                chartHeight={chartHeight}
                tooltipContentStyle={barTooltipContentStyle}
              />
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
                No splats awarded
              </Typography>
            )}
          </>
        )}
      </CardContent>
      <CardActions>
        <Button variant="outlined" component={Link} to="/splats" fullWidth>
          Manage Splats
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default SplatManagementCard;
